import { ActionFunction, redirect } from 'react-router-dom';
import * as Sentry from '@sentry/react';

// GQL
import { client } from '../../../../gql/client';
import { submitSurveillanceRouteEdit } from '../../../../gql/services/surveillanceRouteEdit';

// Types
import { SaveSurveillanceRouteInput } from '../../../../types/surveillanceRoute';

// Constants / utils
import { showSuccessToast } from '../../../../utils/toasts';
import i18n from '../../../../i18n';
import { isInternalPath } from '../../../../utils/internalPath';
import { removeNewSurveillanceRouteStorage } from '../../../../utils/resetData';

// ═══════════════════════════════════════════════════════════════════
// ACTION EditRoute (F4 2026-07-17)
//
// Recibe el `input` ya diffeado por la página (solo campos cambiados + `id`) y
// hace UNA llamada `saveSurveillanceRoute` (update path — `id` presente). Éxito
// → toast + redirect por origen (returnTo interno o detalle). Offline encolado
// → CustomEvent + redirect al listado (espejo EditCampaign). Error → re-lanzado
// al errorElement (nunca `return null` silencioso).
// ═══════════════════════════════════════════════════════════════════

// `isInternalPath` vivía aquí; desde 2026-08-05 está en `utils/internalPath.ts`
// porque lo necesitan también los destinos de vuelta de accidentes y
// comunicaciones.

/** Limpia el borrador de Operarios del wizard de edición tras un guardado OK. */
function clearEditOperatorsDraft(): void {
  removeNewSurveillanceRouteStorage('edit');
}

export const ActionEditRoute: ActionFunction = async ({ request }) => {
  const formData = await request.formData();
  const rawInput = String(formData.get('input') ?? '');
  const returnTo = String(formData.get('returnTo') ?? '');

  let input: SaveSurveillanceRouteInput;
  try {
    input = JSON.parse(rawInput);
  } catch (err) {
    Sentry.captureException(err);
    throw new Error('[EditRoute action] input inválido');
  }

  // `id` de la ENTIDAD SurveillanceRoute (routeId), presente = update path.
  const routeId = input.id ?? '';
  if (!routeId) {
    throw new Error('[EditRoute action] falta el id del recorrido');
  }

  // Idempotencia (espejo del create): clientOpId estable por operación lógica
  // — el replay offline / reintento de red devuelve la misma entidad sin
  // re-ejecutar el update.
  const inputWithOpId: SaveSurveillanceRouteInput = {
    ...input,
    clientOpId: crypto.randomUUID(),
  };

  // returnTo por origen (adenda §1): vuelve al origen si es path interno; si no,
  // al detalle del RV. Se calcula UNA vez y lo usan los dos caminos —guardado
  // online y encolado offline—: hasta 2026-08-05 el offline hardcodeaba
  // `/surveillance-route`, así que editar desde el lápiz del detalle SIN
  // cobertura te sacaba a la tabla mientras que con cobertura te devolvía al
  // detalle. Mismo destino con red y sin ella.
  const destination =
    returnTo && isInternalPath(returnTo)
      ? returnTo
      : `/surveillance-route/${routeId}`;

  try {
    // 🔴 Encolada sin cobertura (2026-09-23): el servicio escribe además el
    // cambio en la caché. Antes solo se encolaba y la tablet seguía viendo el
    // recorrido de antes hasta sincronizar — añadir la vía de apoyo sin red no
    // cambiaba nada en el cronómetro.
    const outcome = await submitSurveillanceRouteEdit(client, inputWithOpId);
    if (outcome === 'queued') {
      // Offline encolado (espejo EditCampaign): al reconectar, syncOfflineQueue
      // replay-ea la mutation con el id en input. No propagamos a ErrorPage.
      showSuccessToast(i18n.t('modals:changeState.queuedOffline'));
      clearEditOperatorsDraft();
      window.dispatchEvent(new CustomEvent('offline-route-queued'));
      return redirect(destination);
    }
  } catch (err) {
    Sentry.captureException(err);
    console.error('> EditRoute - error updating surveillance route:', err);
    // Re-lanzar → errorElement.
    throw err;
  }

  clearEditOperatorsDraft();
  showSuccessToast(i18n.t('toasts:success.updated'));

  return redirect(destination);
};

export default ActionEditRoute;
